/**
 * Passenger / guardian — daily going toggle (default not going), ETA countdown,
 * and browser notifications from manual driver ETA updates (30, 10, 5, 0).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../authContext';
import { localTodayStr } from '../lib/date';
import { partySeatCount } from '../lib/passengers';
import { ASSIGNMENT_LABELS } from '../lib/transport';
import { formatTimeAmPm, goingCutoffLabel, isGoingOpen, isTripSlotActiveOnDate } from '../lib/tripSlots';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

function parseTimeToDateToday(timeStr, now = new Date()) {
  if (!timeStr) return null;
  const parts = String(timeStr).split(':');
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const d = new Date(now);
  d.setHours(hh, mm, 0, 0);
  return d;
}

function msUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime() - Date.now();
  return t;
}

function formatCountdown(ms) {
  if (ms == null || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m ${s}s`;
  }
  return `${m}m ${s}s`;
}

export default function PassengerPortal() {
  const { profile, signOutUser, refresh } = useAuth();
  const navigate = useNavigate();

  const [busId, setBusId] = useState(null);
  const [busLabel, setBusLabel] = useState('');
  const [assignmentStatus, setAssignmentStatus] = useState('not_going');
  const [pickupArea, setPickupArea] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupAddresses, setPickupAddresses] = useState([]);
  const [addrLabelDraft, setAddrLabelDraft] = useState('');
  const [areaDraft, setAreaDraft] = useState('');
  const [addressDraft, setAddressDraft] = useState('');
  const [savingArea, setSavingArea] = useState(false);
  const [adminMessages, setAdminMessages] = useState([]);
  const [partyTotal, setPartyTotal] = useState('1');
  const [partyInfants, setPartyInfants] = useState('0');
  const [seatCount, setSeatCount] = useState(1);
  const [currentEtaMinutes, setCurrentEtaMinutes] = useState(null);
  const [scheduledDepartureTime, setScheduledDepartureTime] = useState(null);
  const [routeStartedAt, setRouteStartedAt] = useState(null);
  const [going, setGoing] = useState(false);
  const [pickedUp, setPickedUp] = useState(false);
  const [etaAt, setEtaAt] = useState(null);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [togglingSlotId, setTogglingSlotId] = useState(null);
  const [tripSlots, setTripSlots] = useState([]);
  const [goingBySlot, setGoingBySlot] = useState({});
  const [error, setError] = useState('');
  const [notifStatus, setNotifStatus] = useState('');

  const arrivedNotifiedRef = useRef(false);
  const prevMinutesRef = useRef(null);
  const lastManualNotifiedRef = useRef(null);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setError('');

    setPickupArea(profile.pickup_area || '');
    setPickupAddress(profile.pickup_address || '');
    setAreaDraft(profile.pickup_area || '');
    setAddressDraft(profile.pickup_address || '');

    const { data: addrRows } = await supabase
      .from('passenger_pickup_addresses')
      .select('id, label, pickup_area, pickup_address, is_active')
      .eq('profile_id', profile.id)
      .order('created_at', { ascending: true });
    setPickupAddresses(addrRows || []);

    const { data: msgRows } = await supabase
      .from('admin_passenger_messages')
      .select('id, message, created_at, passenger_id')
      .order('created_at', { ascending: false })
      .limit(20);

    if (msgRows?.length) {
      const { data: readRows } = await supabase
        .from('admin_passenger_message_reads')
        .select('message_id')
        .eq('passenger_id', profile.id);
      const readSet = new Set((readRows || []).map((r) => r.message_id));
      setAdminMessages((msgRows || []).filter((m) => !readSet.has(m.id)));
    } else {
      setAdminMessages([]);
    }

    const tripDate = localTodayStr();
    let loadedSlots = [];

    if (profile?.org_id) {
      const { data: slots } = await supabase
        .from('org_trip_slots')
        .select('id, label, start_time, trip_date, recurrence_type, days_of_week, day_of_month')
        .eq('org_id', profile.org_id)
        .order('start_time');

      loadedSlots = (slots || []).filter((slot) => isTripSlotActiveOnDate(slot, tripDate));
      setTripSlots(loadedSlots);
    }

    const { data: allGoing, error: allGoingErr } = await supabase
      .from('daily_going')
      .select('id, bus_id, going, picked_up, assignment_status, party_total, party_infants, seat_count, trip_slot_id')
      .eq('passenger_id', profile.id)
      .eq('trip_date', tripDate)
      .eq('going', true);

    if (allGoingErr) {
      setError(allGoingErr.message);
      return;
    }

    const slotMap = {};
    for (const row of allGoing || []) {
      if (row.trip_slot_id) slotMap[row.trip_slot_id] = row;
    }
    setGoingBySlot(slotMap);

    const activeRow = (allGoing || []).find((r) => r.going && r.assignment_status !== 'not_going') || null;

    if (!activeRow) {
      setBusId(null);
      setBusLabel('');
      setGoing(false);
      setPickedUp(false);
      setAssignmentStatus('not_going');
      setCurrentEtaMinutes(null);
      setScheduledDepartureTime(null);
      setRouteStartedAt(null);
      setEtaAt(null);
      return;
    }

    const dg = activeRow;

    setGoing(true);
    setPickedUp(!!dg.picked_up);
    setAssignmentStatus(dg.assignment_status || 'confirmed');
    setBusId(dg.bus_id);
    setPartyTotal(String(dg.party_total ?? 1));
    setPartyInfants(String(dg.party_infants ?? 0));
    setSeatCount(dg.seat_count ?? 1);

    const { data: busRow } = await supabase
      .from('buses')
      .select('label, current_eta_minutes, route_started_at, driver_id')
      .eq('id', dg.bus_id)
      .maybeSingle();
    setBusLabel(busRow?.label || 'Your bus');
    setCurrentEtaMinutes(busRow?.current_eta_minutes ?? null);
    setRouteStartedAt(busRow?.route_started_at || null);

    try {
      if (profile?.org_id && dg.bus_id) {
        const slotForBus = loadedSlots.find((s) => s.id === dg.trip_slot_id);
        if (slotForBus?.start_time) {
          setScheduledDepartureTime(slotForBus.start_time);
        } else {
          const { data: progRows, error: progErr } = await supabase
            .from('programs')
            .select('departure_time')
            .eq('org_id', profile.org_id)
            .eq('bus_id', dg.bus_id)
            .order('departure_time', { ascending: true })
            .limit(1);

          if (!progErr) {
            setScheduledDepartureTime(progRows?.[0]?.departure_time ?? null);
          }
        }
      }
    } catch {
      setScheduledDepartureTime(null);
    }

    const { data: etaRow } = await supabase.from('bus_eta').select('eta_at').eq('bus_id', dg.bus_id).maybeSingle();
    setEtaAt(etaRow?.eta_at || null);
  }, [profile?.id, profile?.org_id, profile?.pickup_area, profile?.pickup_address]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (!busId) return undefined;
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [busId]);

  useEffect(() => {
    if (!busId) return undefined;
    const id = setInterval(() => {
      load();
    }, 15000);
    return () => clearInterval(id);
  }, [busId, load]);

  useEffect(() => {
    if (!busId) return undefined;

    // Subscribe to manual ETA updates written by the driver.
    const channel = supabase
      .channel(`realtime-bus-eta-${busId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buses',
          filter: `id=eq.${busId}`,
        },
        (payload) => {
          const next = payload?.new?.current_eta_minutes ?? null;
          setCurrentEtaMinutes((prev) => {
            if (Object.is(prev, next)) return prev;
            // Supabase sometimes returns numeric columns as numbers; handle strings defensively.
            return typeof next === 'string' ? parseInt(next, 10) : next;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [busId]);

  const remainingMs = useMemo(() => {
    void tick;
    return msUntil(etaAt);
  }, [etaAt, tick]);

  const departureDate = useMemo(() => {
    if (!scheduledDepartureTime) return null;
    void tick;
    return parseTimeToDateToday(scheduledDepartureTime, new Date());
  }, [scheduledDepartureTime, tick]);

  const lateButNotStarted = useMemo(() => {
    if (!departureDate) return false;
    if (routeStartedAt) return false;
    void tick;
    return Date.now() > departureDate.getTime();
  }, [departureDate, routeStartedAt, tick]);

  const etaBanner = useMemo(() => {
    const m = currentEtaMinutes;
    if (m === 30) return { bg: '#1a73e8', fg: '#ffffff', text: 'Your driver is 30 minutes away' };
    if (m === 10)
      return {
        bg: '#fbbc04',
        fg: '#1b1f23',
        text: 'Your driver is 10 minutes away — please get ready',
      };
    if (m === 5) return { bg: '#ff9800', fg: '#1b1f23', text: 'Driver is 5 minutes away — head to your pickup point' };
    if (m === 0) return { bg: '#34a853', fg: '#ffffff', text: 'Your driver has arrived!' };
    return null;
  }, [currentEtaMinutes]);

  const todayKey = localTodayStr();
  useEffect(() => {
    arrivedNotifiedRef.current = false;
    prevMinutesRef.current = null;
    lastManualNotifiedRef.current = null;
  }, [etaAt, going, todayKey, busId]);

  useEffect(() => {
    if (currentEtaMinutes != null) return;
    if (!going || !etaAt || remainingMs == null) return;

    const tryNotify = (title, body, key) => {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      try {
        new Notification(title, { body, tag: `tt-${key}-${localTodayStr()}`, renotify: true });
      } catch {
        /* ignore */
      }
    };

    if (remainingMs <= 0) {
      if (!arrivedNotifiedRef.current) {
        arrivedNotifiedRef.current = true;
        tryNotify('Transport Team', 'Bus has arrived.', 'arrived');
      }
      return;
    }

    const minutes = remainingMs / 60000;
    const prev = prevMinutesRef.current;

    if (prev == null) {
      prevMinutesRef.current = minutes;
      return;
    }

    if (prev > 10 && minutes <= 10) {
      tryNotify('Transport Team', 'About 10 minutes until arrival.', '10');
    }
    if (prev > 5 && minutes <= 5) {
      tryNotify('Transport Team', 'About 5 minutes until arrival.', '5');
    }
    if (prev > 2 && minutes <= 2) {
      tryNotify('Transport Team', 'About 2 minutes until arrival.', '2');
    }

    prevMinutesRef.current = minutes;
  }, [going, etaAt, remainingMs, currentEtaMinutes]);

  useEffect(() => {
    if (currentEtaMinutes == null) return;
    if (![30, 10, 5, 0].includes(currentEtaMinutes)) return;
    if (lastManualNotifiedRef.current === currentEtaMinutes) return;

    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;

    const mapping = {
      30: 'Your driver is 30 minutes away',
      10: 'Your driver is 10 minutes away — please get ready',
      5: 'Driver is 5 minutes away — head to your pickup point',
      0: 'Your driver has arrived!',
    };

    lastManualNotifiedRef.current = currentEtaMinutes;
    try {
      new Notification('Transport Team', { body: mapping[currentEtaMinutes], tag: `manual-eta-${currentEtaMinutes}-${localTodayStr()}`, renotify: true });
    } catch {
      /* ignore */
    }
  }, [currentEtaMinutes]);

  async function requestNotifications() {
    if (typeof Notification === 'undefined') {
      setNotifStatus('Notifications are not supported in this browser.');
      return;
    }
    const perm = await Notification.requestPermission();
    setNotifStatus(perm === 'granted' ? 'Notifications enabled.' : 'Notifications not granted.');
  }

  async function addPickupAddress(e) {
    e.preventDefault();
    if (!profile?.id) return;
    if (!areaDraft.trim()) {
      setError('Pickup area is required.');
      return;
    }
    if (pickupAddresses.length >= 5) {
      setError('You can save up to 5 pickup addresses.');
      return;
    }
    setSavingArea(true);
    setError('');
    const isFirst = pickupAddresses.length === 0;
    const { data: inserted, error: err } = await supabase
      .from('passenger_pickup_addresses')
      .insert({
        profile_id: profile.id,
        label: addrLabelDraft.trim() || null,
        pickup_area: areaDraft.trim(),
        pickup_address: addressDraft.trim() || null,
        is_active: isFirst,
      })
      .select('id')
      .single();
    setSavingArea(false);
    if (err) {
      setError(err.message);
      return;
    }
    if (isFirst && inserted?.id) {
      await supabase.rpc('set_active_pickup_address', { p_address_id: inserted.id });
    }
    setAddrLabelDraft('');
    setAreaDraft('');
    setAddressDraft('');
    await refresh();
    await load();
  }

  async function activatePickupAddress(addressId) {
    setError('');
    const { error: err } = await supabase.rpc('set_active_pickup_address', { p_address_id: addressId });
    if (err) {
      setError(err.message);
      return;
    }
    await refresh();
    await load();
  }

  async function removePickupAddress(addressId) {
    setError('');
    const { error: err } = await supabase.rpc('delete_pickup_address', { p_address_id: addressId });
    if (err) {
      setError(err.message);
      return;
    }
    await refresh();
    await load();
  }

  async function dismissAdminMessage(messageId) {
    const { error: err } = await supabase.rpc('mark_admin_message_read', { p_message_id: messageId });
    if (err) {
      setError(err.message);
      return;
    }
    setAdminMessages((prev) => prev.filter((m) => m.id !== messageId));
  }

  async function toggleGoingForSlot(slot, next) {
    if (!profile?.id || togglingSlotId) return;
    setError('');
    setTogglingSlotId(slot.id);
    const tripDate = localTodayStr();

    try {
      const total = parseInt(partyTotal, 10) || 1;
      const infants = parseInt(partyInfants, 10) || 0;
      if (next && total < 1) {
        setError('Party size must be at least 1.');
        return;
      }
      if (next && infants > total) {
        setError('Infants cannot exceed total people.');
        return;
      }
      if (next && !isGoingOpen(slot.start_time, slot.trip_date || tripDate)) {
        setError(`Too late — cutoff was ${goingCutoffLabel(slot.start_time, slot.trip_date || tripDate)} for this trip.`);
        return;
      }

      const { data, error: err } = await supabase.rpc('set_passenger_going', {
        p_going: next,
        p_trip_date: tripDate,
        p_party_total: next ? total : 1,
        p_party_infants: next ? infants : 0,
        p_trip_slot_id: slot.id,
      });

      if (err) {
        setError(err.message);
        return;
      }

      if (data) {
        setGoing(!!data.going);
        setAssignmentStatus(data.assignment_status || (next ? 'confirmed' : 'not_going'));
        if (data.bus_label) setBusLabel(data.bus_label);
        if (data.seat_count != null) setSeatCount(data.seat_count);
      }
      await load();
    } finally {
      setTogglingSlotId(null);
    }
  }

  const computedSeats = useMemo(() => partySeatCount(partyTotal), [partyTotal]);

  async function handleSignOut() {
    await signOutUser();
    navigate('/login', { replace: true });
  }

  if (loading) {
    return (
      <div style={shell}>
        <p style={{ color: muted }}>Loading passenger portal…</p>
      </div>
    );
  }

  return (
    <div style={shell}>
      <header style={headerBar}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.2rem, 4vw, 1.45rem)' }}>Passenger</h1>
          <p style={{ margin: '4px 0 0', color: muted, fontSize: '0.9rem' }}>
            {profile?.full_name || 'Welcome'}
          </p>
        </div>
        <button type="button" onClick={handleSignOut} style={ghostBtn}>
          Sign out
        </button>
      </header>

      {error ? (
        <p style={{ color: '#f28b82', marginBottom: 12 }} role="alert">
          {error}
        </p>
      ) : null}

      {adminMessages.length > 0 ? (
        <section style={section}>
          <h2 style={h2}>Message from admin</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {adminMessages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  ...cardBox,
                  borderColor: 'rgba(13,115,119,0.45)',
                  background: 'rgba(13,115,119,0.1)',
                }}
              >
                <p style={{ margin: '0 0 8px', lineHeight: 1.5 }}>{msg.message}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: muted, fontSize: '0.8rem' }}>{new Date(msg.created_at).toLocaleString()}</span>
                  <button type="button" onClick={() => dismissAdminMessage(msg.id)} style={ghostBtn}>
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section style={section}>
        <h2 style={h2}>Pickup addresses</h2>
        <div style={cardBox}>
          <p style={{ marginTop: 0, color: muted, fontSize: '0.88rem' }}>
            Save up to 5 addresses. Toggle which one you want to be picked up from today.
          </p>
          {pickupAddresses.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {pickupAddresses.map((addr) => (
                <div
                  key={addr.id}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: addr.is_active ? `1px solid ${accent}` : '1px solid rgba(255,255,255,0.08)',
                    background: addr.is_active ? 'rgba(13,115,119,0.12)' : 'rgba(0,0,0,0.2)',
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      {addr.label ? (
                        <strong style={{ display: 'block', marginBottom: 4 }}>{addr.label}</strong>
                      ) : null}
                      <span>
                        {addr.pickup_area}
                        {addr.pickup_address ? ` · ${addr.pickup_address}` : ''}
                      </span>
                      {addr.is_active ? (
                        <span style={{ display: 'block', color: accent, fontSize: '0.82rem', marginTop: 4, fontWeight: 600 }}>
                          Active pickup
                        </span>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!addr.is_active ? (
                        <button type="button" onClick={() => activatePickupAddress(addr.id)} style={ghostBtn}>
                          Use this
                        </button>
                      ) : null}
                      <button type="button" onClick={() => removePickupAddress(addr.id)} style={ghostBtn}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: muted, fontSize: '0.88rem' }}>No addresses saved yet — add your first pickup location below.</p>
          )}
          {pickupAddresses.length < 5 ? (
            <form onSubmit={addPickupAddress}>
              <label style={{ fontSize: '0.85rem', color: muted }}>Label (optional)</label>
              <input
                value={addrLabelDraft}
                onChange={(e) => setAddrLabelDraft(e.target.value)}
                style={{ ...inputStyle, marginTop: 6, marginBottom: 12 }}
                placeholder="e.g. Home, School"
              />
              <label style={{ fontSize: '0.85rem', color: muted }}>Area</label>
              <input
                required
                value={areaDraft}
                onChange={(e) => setAreaDraft(e.target.value)}
                style={{ ...inputStyle, marginTop: 6, marginBottom: 12 }}
                placeholder="e.g. Gateshead, Lekki"
              />
              <label style={{ fontSize: '0.85rem', color: muted }}>Address (optional)</label>
              <input
                value={addressDraft}
                onChange={(e) => setAddressDraft(e.target.value)}
                style={{ ...inputStyle, marginTop: 6, marginBottom: 12 }}
              />
              <button type="submit" disabled={savingArea} style={primaryBtn}>
                {savingArea ? 'Saving…' : 'Add address'}
              </button>
            </form>
          ) : (
            <p style={{ color: muted, fontSize: '0.85rem', margin: 0 }}>Maximum of 5 addresses reached. Delete one to add another.</p>
          )}
        </div>
      </section>

      {!pickupArea ? (
        <section style={section}>
          <p style={{ color: '#fbbc04', fontSize: '0.9rem' }}>
            Add and activate a pickup address above before marking going for a trip.
          </p>
        </section>
      ) : (
        <>
          <section style={section}>
            <h2 style={h2}>Today&apos;s trip</h2>
            <div style={cardBox}>
              <p style={{ marginTop: 0, fontSize: '0.95rem' }}>
                Pickup from: <strong style={{ color: accent }}>{pickupArea}</strong>
                {pickupAddress ? ` · ${pickupAddress}` : ''}
              </p>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <p style={{ margin: '0 0 10px', color: muted, fontSize: '0.88rem' }}>
                  How many people are going? Everyone uses a seat, including children aged 3 and under.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: muted }}>Total people</label>
                    <input
                      type="number"
                      min={1}
                      value={partyTotal}
                      onChange={(e) => setPartyTotal(e.target.value)}
                      style={{ ...inputStyle, marginTop: 6 }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: muted }}>Aged 3 and under</label>
                    <input
                      type="number"
                      min={0}
                      value={partyInfants}
                      onChange={(e) => setPartyInfants(e.target.value)}
                      style={{ ...inputStyle, marginTop: 6 }}
                    />
                  </div>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: '0.85rem', color: muted }}>
                  Seats needed: <strong style={{ color: text }}>{computedSeats}</strong>
                </p>
              </div>

              <p style={{ color: muted, fontSize: '0.88rem', marginBottom: 12 }}>
                Mark going for each trip you need. Cutoff is 90 minutes before start time.
              </p>

              {tripSlots.length === 0 ? (
                <p style={{ color: '#fbbc04', fontSize: '0.9rem' }}>No trips scheduled today — ask your admin to add trip start times.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {tripSlots.map((slot) => {
                    const tripDate = localTodayStr();
                    const slotDate = slot.trip_date || tripDate;
                    const slotGoing = !!goingBySlot[slot.id];
                    const slotRow = goingBySlot[slot.id];
                    const open = isGoingOpen(slot.start_time, slotDate);
                    const busy = togglingSlotId === slot.id;
                    return (
                      <div
                        key={slot.id}
                        style={{
                          padding: '14px 16px',
                          borderRadius: 12,
                          background: 'rgba(0,0,0,0.22)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                          <div>
                            <p style={{ margin: 0, fontWeight: 600 }}>{slot.label}</p>
                            <p style={{ margin: '4px 0 0', color: muted, fontSize: '0.82rem' }}>
                              Starts {formatTimeAmPm(slot.start_time, slotDate)}
                              {open ? ` · Mark going until ${goingCutoffLabel(slot.start_time, slotDate)}` : ' · Cutoff passed'}
                            </p>
                            {slotGoing && slotRow ? (
                              <p
                                style={{
                                  margin: '8px 0 0',
                                  fontSize: '0.82rem',
                                  color: slotRow.assignment_status === 'waitlisted' ? '#fbbc04' : '#81c995',
                                  fontWeight: 600,
                                }}
                              >
                                {ASSIGNMENT_LABELS[slotRow.assignment_status] || slotRow.assignment_status}
                              </p>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={slotGoing}
                            disabled={busy || (!slotGoing && !open)}
                            onClick={() => toggleGoingForSlot(slot, !slotGoing)}
                            style={{
                              ...toggleTrackStyle,
                              background: slotGoing ? accent : 'rgba(255,255,255,0.18)',
                              opacity: busy || (!slotGoing && !open) ? 0.5 : 1,
                              cursor: busy || (!slotGoing && !open) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <span
                              style={{
                                ...toggleThumbStyle,
                                transform: slotGoing ? 'translateX(22px)' : 'translateX(0)',
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {going && busLabel ? (
                <p style={{ marginTop: 16, marginBottom: 0, fontSize: '0.95rem' }}>
                  Active bus: <strong style={{ color: accent }}>{busLabel}</strong>
                  {' · '}
                  {seatCount} seat{seatCount !== 1 ? 's' : ''} ({partyTotal} people
                  {parseInt(partyInfants, 10) > 0 ? `, ${partyInfants} aged 3 and under` : ''})
                </p>
              ) : null}
              {going && assignmentStatus ? (
                <p
                  style={{
                    marginTop: 10,
                    marginBottom: 0,
                    fontSize: '0.88rem',
                    color: assignmentStatus === 'waitlisted' ? '#fbbc04' : assignmentStatus === 'confirmed' ? '#81c995' : muted,
                    fontWeight: 600,
                  }}
                >
                  {ASSIGNMENT_LABELS[assignmentStatus] || assignmentStatus}
                </p>
              ) : null}
              {scheduledDepartureTime && going && assignmentStatus === 'confirmed' ? (
                <p style={{ marginTop: 8, marginBottom: 6, color: muted, fontSize: '0.88rem' }}>
                  Scheduled departure: <strong style={{ color: text }}>{formatTimeAmPm(scheduledDepartureTime, localTodayStr())}</strong>
                </p>
              ) : null}

              {pickedUp && assignmentStatus === 'confirmed' ? (
                <p style={{ marginTop: 16, marginBottom: 0, color: '#81c995', fontSize: '0.9rem' }}>
                  Driver marked you as picked up.
                </p>
              ) : null}

              {lateButNotStarted && assignmentStatus === 'confirmed' ? (
                <div
                  style={{
                    marginTop: 14,
                    background: 'rgba(251,188,4,0.12)',
                    border: '1px solid rgba(251,188,4,0.45)',
                    borderRadius: 12,
                    padding: '12px 14px',
                    color: '#fbbc04',
                    fontWeight: 700,
                  }}
                  role="alert"
                >
                  Running slightly late - please wait
                </div>
              ) : null}
            </div>
          </section>

          {going && assignmentStatus === 'confirmed' ? (
            <section style={{ ...section, paddingBottom: 40 }}>
              <h2 style={h2}>ETA</h2>
              <div style={cardBox}>
                <p style={{ marginTop: 0, color: muted, fontSize: '0.9rem' }}>
                  Driver alerts update in real-time (30, 10, 5, and on arrival). Enable notifications to also get browser alerts.
                </p>

                {etaBanner ? (
                  <div
                    style={{
                      marginBottom: 12,
                      background: etaBanner.bg,
                      color: etaBanner.fg,
                      borderRadius: 12,
                      padding: '12px 14px',
                      border: `1px solid rgba(13,115,119,0.35)`,
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.03) inset',
                    }}
                    role="alert"
                  >
                    <strong style={{ display: 'block', fontSize: '1.05rem', lineHeight: 1.3 }}>{etaBanner.text}</strong>
                  </div>
                ) : null}

                <div
                  style={{
                    fontSize: '2rem',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: remainingMs != null && remainingMs <= 0 ? '#81c995' : accent,
                    marginBottom: 12,
                  }}
                >
                  {remainingMs != null && remainingMs <= 0 ? 'Arrived / due' : formatCountdown(remainingMs)}
                </div>

                {!etaAt ? (
                  <p style={{ color: muted, fontSize: '0.88rem', margin: 0 }}>Waiting for driver ETA…</p>
                ) : null}

                <button type="button" onClick={requestNotifications} style={ghostBtn}>
                  Enable notifications
                </button>
                {notifStatus ? (
                  <p style={{ color: muted, fontSize: '0.85rem', marginTop: 10, marginBottom: 0 }}>{notifStatus}</p>
                ) : null}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

const shell = {
  minHeight: '100vh',
  background: bg,
  color: text,
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  padding: '20px 16px 40px',
  maxWidth: 560,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const headerBar = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 24,
  paddingBottom: 16,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const section = { marginBottom: 24 };
const h2 = { fontSize: '1.1rem', margin: '0 0 10px' };

const cardBox = {
  background: card,
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.06)',
};

const primaryBtn = {
  padding: '12px 18px',
  borderRadius: 10,
  border: 'none',
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent',
  color: text,
  cursor: 'pointer',
};

const toggleTrackStyle = {
  position: 'relative',
  width: 52,
  height: 30,
  borderRadius: 999,
  border: 'none',
  padding: 0,
  flexShrink: 0,
  transition: 'background 0.2s ease',
};

const toggleThumbStyle = {
  position: 'absolute',
  top: 3,
  left: 3,
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
  transition: 'transform 0.2s ease',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f1419',
  color: text,
  fontSize: '0.95rem',
};
